// Legenda van het Dagstart-rooster (Joris 7 okt: "wat betekent een kleur?"). Eén as: kleur =
// soort werk; plus de ruitjes, de arceringen en de getallen onderin een dag. Sinds 4 nov altijd
// achter één knop ("anders te druk het scherm"), ook op een breed scherm.
import React, { useState } from 'react';
import { Box, Button, Popover, Typography } from '@mui/material';
import { WERKSOORTEN, werkSoortStijl, V1_ACCENT, MIJLPAAL_GEDEMPT } from '../../utils/roosterBetekenis';

const GEDEMPT = '#6b7280';
const tekst = { fontSize: 11, color: GEDEMPT, lineHeight: 1.2, whiteSpace: 'nowrap' };

const voorbeeldTaak = {
  voorbereiding: { name: 'Script' },
  animatie: { name: 'Animatie' },
  montage: { name: 'Montage' },
  draaidag: { type: 'Draaidag' },
  overig: { name: 'Overig', status: 'Vrij/intern' },
  afspraak: { type: 'Afspraak' },
};

function Ruit({ kleur, grootte }) {
  return (
    <Box component="span" aria-hidden sx={{
      display: 'inline-block', width: grootte, height: grootte, bgcolor: kleur, borderRadius: '1px',
      transform: 'rotate(45deg)', flexShrink: 0, mx: '2px',
    }} />
  );
}

function Regel({ voorbeeld, tekst: uitleg }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <Box sx={{ width: 44, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>{voorbeeld}</Box>
      <Typography component="span" sx={{ ...tekst, whiteSpace: 'normal', maxWidth: 240 }}>{uitleg}</Typography>
    </Box>
  );
}

function Inhoud({ kolom = false }) {
  return (
    <>
      {WERKSOORTEN.map((s) => {
        const stijl = werkSoortStijl(voorbeeldTaak[s.key]);
        return (
          <Box key={s.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box component="span" aria-hidden sx={{
              display: 'inline-block', flexShrink: 0, width: 14, height: 10, borderRadius: '3px', bgcolor: stijl.vlak,
              boxShadow: s.key === 'afspraak' ? 'none' : `inset 3px 0 0 ${stijl.streep}`,
            }} />
            <Typography component="span" sx={tekst}>{s.label}</Typography>
          </Box>
        );
      })}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: kolom ? 'wrap' : 'nowrap' }} title="Het ruitje staat op de einddag van de taak; het label ervoor is de versie van die taak">
        <Box component="span" sx={{ px: '5px', borderRadius: '4px', bgcolor: V1_ACCENT, color: '#fff', fontSize: 10, fontWeight: 700, lineHeight: '14px' }}>v1</Box>
        <Ruit kleur={V1_ACCENT} grootte={7} />
        <Typography component="span" sx={tekst}>eerste versie naar klant</Typography>
        <Ruit kleur={MIJLPAAL_GEDEMPT} grootte={5} />
        <Typography component="span" sx={tekst}>andere versie</Typography>
      </Box>
      {kolom && (
        <Typography sx={{ ...tekst, whiteSpace: 'normal', maxWidth: 260 }}>
          Het ruitje staat op de einddag van de taak; het label ervoor is de versie van díe taak.
        </Typography>
      )}
      {kolom && (
        <>
          <Box sx={{ height: 1, bgcolor: '#eceef1', my: 0.5 }} />
          <Regel voorbeeld={<Box aria-hidden sx={{ width: 28, height: 12, borderRadius: '3px', bgcolor: werkSoortStijl(voorbeeldTaak.animatie).vlak,
            background: `repeating-linear-gradient(135deg, rgba(255,255,255,0.95) 0 3px, transparent 3px 6px) left bottom / 100% 5px no-repeat, linear-gradient(rgba(255,255,255,0.5), rgba(255,255,255,0.5)), ${werkSoortStijl(voorbeeldTaak.animatie).vlak}` }} />}
            tekst="Arcering onderaan een balk: gepland op een dag dat iemand afwezig is" />
          <Regel voorbeeld={<Box aria-hidden sx={{ width: 28, height: 12, borderRadius: '3px',
            background: 'repeating-linear-gradient(135deg, #ecfdf5 0 5px, #c7ead9 5px 10px)' }} />}
            tekst="Schuin gearceerd vlak (in de kleur van het werk): klant reviewt — wachten op feedback tussen twee versies" />
          <Regel voorbeeld={<Typography component="span" sx={{ fontSize: 11, fontWeight: 700, color: '#dc2626' }}>Afwezig</Typography>}
            tekst="Rood 'Afwezig' of 'Draaidag' onderin een dag: vrij of op een draaidag — er kan niets bij" />
          <Regel voorbeeld={<Typography component="span" sx={{ fontSize: 11 }}><Box component="span" sx={{ color: '#15803d' }}>+3u</Box> <Box component="span" sx={{ color: '#dc2626', fontWeight: 700 }}>−2u</Box></Typography>}
            tekst="Onderin een dag: nog vrij · te veel gepland" />
        </>
      )}
    </>
  );
}

/** Eén knop "Legenda" met een popover — rustig, ook op een breed scherm (Joris 4 nov). */
export default function RoosterLegenda({ sx }) {
  const [anker, setAnker] = useState(null);
  return (
    <>
      <Button size="small" data-testid="rooster-legenda" onClick={(e) => setAnker(e.currentTarget)} aria-haspopup="dialog" aria-expanded={!!anker}
        sx={{ textTransform: 'none', fontSize: 12, color: GEDEMPT, mr: 0.5, ...sx }}>
        Legenda
      </Button>
      <Popover open={!!anker} anchorEl={anker} onClose={() => setAnker(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Box role="dialog" aria-label="Legenda" sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <Inhoud kolom />
        </Box>
      </Popover>
    </>
  );
}
